import { CostEventsDataGridApi } from "src/api/generated/erp/common/costEvents/api/costEventsDataGridApi.ts";
import { dateColumn, dateTimeColumn, textColumn } from "../../../../common/dataGrid/columns.tsx";
import i18n from "i18next";
import { CostEventView } from "src/api/generated/erp/db/types/tables/costEventView.ts";
import { CostEventObjectRef } from "src/api/generated/erp/common/costEvents/model/costEventObjectRef.ts";
import { CostEventForm } from "./CostEventForm.tsx";
import { FormCrudDataGrid } from "../../../../common/dataGrid/crud/FormCrudDataGrid.tsx";
import { Typography } from "@mui/material";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { formatMoney } from "src/utils/numberUtils.ts";
import { AavoGridColDef } from "src/components/common/dataGrid/types";
import { ServerSideDataModelResult } from "src/api/generated/common/dataGrids/serverSideDataModelResult.ts";
import {
	ParamsWithDataModelRequest,
	ServerSideDataGridModel,
	ServerSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import React from "react";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CostEventsDataGridBaseProps<
	TRow extends CostEventView,
	TParams extends object,
	TApiResponse extends CostEventsDataGridApiResponse<TRow>,
> {
	fetchData: (params: ParamsWithDataModelRequest<TParams>) => Promise<TApiResponse>;
	initialParams: TParams;
	gridId: string;
	editEnabled?: boolean | ((row: TRow) => string | undefined);
	disableDelete?: boolean;
	insert?:
		| { type: "disabled" }
		| {
				type: "enabled";
				newEventObjectRef: CostEventObjectRef;
				newEventPriceListId: number;
		  };
	showTotals?: boolean;
	showObjectDescription?: boolean;
	extraColumns?: AavoGridColDef<TRow>[];
	refreshRef?: RefreshableElementRef;
	actionBarComponents?: (
		params: Pick<
			ServerSideDataGridModelRenderProps<TApiResponse, CostEventView, TParams>,
			"currentParams" | "refreshData"
		>,
	) => React.ReactNode;
	actionBarMenuComponents?: (
		params: Pick<
			ServerSideDataGridModelRenderProps<TApiResponse, CostEventView, TParams>,
			"currentParams" | "refreshData"
		>,
	) => React.ReactNode;
}

interface CostEventsDataGridApiResponse<TRow extends CostEventView> {
	costEventsData: ServerSideDataModelResult<TRow>;
	totalCosts: number;
	totalPrice: number;
}

export const CostEventsDataGridBase = <
	TRow extends CostEventView,
	TParams extends object,
	TApiResponse extends CostEventsDataGridApiResponse<TRow>,
>({
	fetchData,
	editEnabled: editEnabledProp = true,
	disableDelete = false,
	insert: insertConfig,
	gridId,
	showTotals = true,
	showObjectDescription = false,
	extraColumns,
	initialParams,
	refreshRef,
	actionBarComponents,
	actionBarMenuComponents,
}: CostEventsDataGridBaseProps<TRow, TParams, TApiResponse>) => {
	const editEnabled =
		typeof editEnabledProp === "function" ? (row: CostEventView) => editEnabledProp(row as TRow) : editEnabledProp;
	return (
		<ServerSideDataGridModel<TApiResponse, CostEventView, TParams>
			fetchData={fetchData}
			getDataModelResult={(data) => data.costEventsData}
			getRowId={(row) => row.costEventId}
			gridId={gridId}
			initialParams={initialParams}
			refreshRef={refreshRef}
			render={({ dataGridProps, currentParams, refreshData, data: { totalCosts, totalPrice } }) => {
				return (
					<FormCrudDataGrid<CostEventView>
						listViewOnMobile
						columns={[
							showObjectDescription &&
								textColumn({
									headerName: i18n.t("target"),
									field: "objectDescription",
									width: 200,
									mobile: "cell",
								}),
							textColumn({
								headerName: i18n.t("item"),
								field: "itemName",
								mobile: "header",
							}),
							textColumn({
								headerName: i18n.t("quantity"),
								field: "quantity",
								mobile: "cell",
							}),
							dateColumn({
								headerName: i18n.t("date_short"),
								field: "eventDate",
								mobile: "cell",
							}),
							textColumn({
								headerName: i18n.t("description"),
								field: "description",
								width: 400,
								mobile: "header",
							}),
							textColumn({
								headerName: i18n.t("explanation.cost_event"),
								field: "explanation",
								mobile: "cell",
							}),
							textColumn({
								headerName: i18n.t("cost"),
								field: "cost",
								mobile: "cell",
							}),
							textColumn({
								headerName: i18n.t("price"),
								field: "price",
							}),
							textColumn({
								headerName: i18n.t("total_cost"),
								field: "totalCost",
								width: 140,
							}),
							textColumn({
								headerName: i18n.t("total_price"),
								field: "totalPrice",
							}),
							textColumn({
								headerName: i18n.t("vat_code"),
								field: "vatCodeName",
							}),
							textColumn({
								headerName: i18n.t("created_by"),
								field: "createdByUserName",
							}),
							dateTimeColumn({
								headerName: i18n.t("created_at"),
								field: "createdAt",
								width: 150,
							}),
							// Can't make TypeScript happy here.
							(extraColumns ?? []) as AavoGridColDef<CostEventView>[],
						]}
						form={{
							dialogSize: "md",
							dialogTitle: i18n.t("cost_event"),
							editEnabled: editEnabled,
							addRowEnabled: insertConfig?.type === "enabled",
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.common.costEvent.formDialog"),
								pathSegments:
									row == null ?
										[i18n.t("cost_event")]
									:	[`${i18n.t("cost_event")} ${row.costEventId}`],
								data: {
									costEventId: row?.costEventId ?? null,
									objectType:
										row?.objectType ??
										(insertConfig?.type === "enabled" ?
											insertConfig.newEventObjectRef.objectType
										:	null),
									objectId:
										row?.objectId ??
										(insertConfig?.type === "enabled" ?
											insertConfig.newEventObjectRef.objectId
										:	null),
								},
							}),
							component: ({ row, onCompleted, onFormEdited }) => {
								return (
									<CostEventForm
										costEventId={row?.costEventId}
										onCompleted={onCompleted}
										onFormEdited={onFormEdited}
										costEventPriceListId={
											insertConfig?.type === "enabled" ?
												insertConfig.newEventPriceListId
											:	undefined
										}
										newCostEventObjectRef={
											insertConfig?.type === "enabled" ?
												insertConfig.newEventObjectRef
											:	undefined
										}
									/>
								);
							},
						}}
						remove={
							!disableDelete && {
								type: "enabled",
								action: ({ items }) => CostEventsDataGridApi.delete_({ costEvents: items }),
							}
						}
						actionBarComponents={
							<>
								{actionBarComponents?.({ currentParams, refreshData })}
								{showTotals && (
									<HorizontalBox sx={{ alignItems: "center", gap: 3, ml: 1 }}>
										<Typography>{`${i18n.t("price")} ${formatMoney(totalPrice)}`}</Typography>
										<Typography>{`${i18n.t("cost")} ${formatMoney(totalCosts)}`}</Typography>
									</HorizontalBox>
								)}
							</>
						}
						actionBarMenuComponents={actionBarMenuComponents?.({ currentParams, refreshData })}
						agentContext={{
							key: defineAgentUiContextKey("erp.common.costEvents.grid"),
							rowFields: ["costEventId", "itemId", "objectType", "objectId", "costEventPriceListId"],
							selectedRowPathSegment: (row) => `${i18n.t("cost_event")} ${row.costEventId}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
